// `/live-in-club` — the V1 Manager-facing Live In Club surface.
// Route is a primary adapter per ADR 0005: it registers the path,
// warms the Tanstack Query cache via a non-blocking route loader
// (the page shows a roster skeleton until the query resolves),
// reads the tenant scope from the parent `_authenticated` route
// context, and delegates UI to `LiveInClubPage` in the live-in-club
// bounded context's `ui/` layer.

import { createFileRoute, useRouteContext } from '@tanstack/react-router';

import { useFeatureFlag } from '~/_shared/feature-flags/react/use-feature-flag';
import {
	getClubSettingsQueryKey,
	getClubSettingsServerFn,
} from '~/bounded-contexts/club/get-club-settings.fn';
import { listLiveInClubServerFn } from '~/bounded-contexts/live-in-club/application/list-live-in-club.fn';
import {
	LiveInClubPage,
	liveInClubQueryKey,
} from '~/bounded-contexts/live-in-club/ui/live-in-club-page/live-in-club-page';

function LiveInClubRouteComponent() {
	const ctx = useRouteContext({ from: '/_authenticated' });
	// Read the global auto-clockout kill-switch here (inside the router) and pass
	// it down, so LiveInClubPage stays router-free and unit-testable (#10).
	const auto_clockout_enabled = useFeatureFlag('auto-clockout', false);
	return (
		<LiveInClubPage
			club_id={ctx?.scope?.club?.id ?? ''}
			club_name={ctx?.scope?.club?.name ?? ''}
			auto_clockout_enabled={auto_clockout_enabled}
		/>
	);
}

export const Route = createFileRoute('/_authenticated/live-in-club')({
	loader: async ({ context }) => {
		const clubId = context.scope.club.id;
		// On the SERVER the prefetches are awaited: whatever settles during render
		// dehydrates, so a fire-and-forget here would race dehydration and hand the
		// client a first render (data) that disagrees with the server HTML
		// (skeleton) — a hydration mismatch. Awaiting makes the SSR document carry
		// the real roster. On the CLIENT they stay fire-and-forget so a warm nav
		// never blocks: the page renders the roster skeleton until the query
		// resolves, and the settings read populates the auto-clockout banner on its
		// own cadence. Each prefetch swallows its own rejection so neither surfaces
		// as an unhandled promise.
		const roster = context.queryClient
			.prefetchQuery({
				queryKey: liveInClubQueryKey(clubId),
				queryFn: () => listLiveInClubServerFn(),
			})
			.catch(() => {});
		const settings = context.queryClient
			.prefetchQuery({
				queryKey: getClubSettingsQueryKey(clubId),
				queryFn: () => getClubSettingsServerFn(),
			})
			.catch(() => {});
		if (typeof window === 'undefined') {
			await Promise.all([roster, settings]);
		}
	},
	component: LiveInClubRouteComponent,
});
